import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { DefaultSettingsAdapter } from '../components/PackagePicker/DefaultSettingsAdapter';
import { PlansInTabsViewState, PlansInTabsViewWriter } from '../services/plans-in-tabs-view-storage';
import { safeParse } from './common';
import { AnyPlan } from './plan-unified-utils';
import { getServerVisibleVariants, VariantsByPlan } from './variant-visibility';

export function getPlanListEntriesForTab({
  plans,
  activeTabName,
  plansInTabsView,
  selectedTabNames,
}: {
  plans: Plan[];
  activeTabName: string;
  plansInTabsView: string[];
  selectedTabNames: string[];
}): {
  plans: Plan[];
  variantEntries: VariantsByPlan;
} {
  const allPlansById = new Map(plans.map((plan) => [plan.id!, plan]));

  const eligibleInUserOrder = plansInTabsView.flatMap((planId) => {
    const plan = allPlansById.get(planId);

    if (!plan || !getIsPlanEligibleForTabsView(plan, selectedTabNames)) {
      return [];
    }

    const matchingVariant = getServerVisibleVariants(plan).find((variant) => variant.name === activeTabName);

    return matchingVariant ? [{ plan, variant: matchingVariant }] : [];
  });

  return {
    plans: eligibleInUserOrder.map(({ plan }) => plan),
    variantEntries: Object.fromEntries(eligibleInUserOrder.map(({ plan, variant }) => [plan.id!, [variant]])),
  };
}

export function deriveVariantTabNames(plans: Plan[]): string[] {
  return plans.reduce<string[]>((collectedTabNames, plan) => {
    const namedVisibleVariants = getServerVisibleVariants(plan).filter((variant) => Boolean(variant.name));

    if (namedVisibleVariants.length < 2) {
      return collectedTabNames;
    }

    return namedVisibleVariants.reduce<string[]>(
      (tabNames, variant) => (tabNames.includes(variant.name!) ? tabNames : [...tabNames, variant.name!]),
      collectedTabNames,
    );
  }, []);
}

export function getIsPlanEligibleForTabsView(plan: Plan, selectedTabNames: string[]): boolean {
  const namedVisibleVariantNames = getServerVisibleVariants(plan).map((variant) => variant.name);

  if (namedVisibleVariantNames.length < 2) {
    return false;
  }

  return selectedTabNames.every((tabName) => namedVisibleVariantNames.includes(tabName));
}

export function pruneStaleTabNames(availableTabNames: string[], selectedTabs: string[]): string[] {
  return selectedTabs.filter((tabName) => availableTabNames.includes(tabName));
}

export function pruneStaleVisibleTabs(plans: AnyPlan[], adaptedSettings: DefaultSettingsAdapter): string[] {
  const selectedTabs = safeParse<string[]>(adaptedSettings.get('visibleTabs'), []);

  if (!selectedTabs.length) {
    return [];
  }

  const availableTabNames = deriveVariantTabNames(plans as Plan[]);
  const prunedSelectedTabs = pruneStaleTabNames(availableTabNames, selectedTabs);

  if (prunedSelectedTabs.length !== selectedTabs.length) {
    adaptedSettings.set('visibleTabs', JSON.stringify(prunedSelectedTabs));
  }

  return prunedSelectedTabs;
}

export function getOrderedVisibleTabsViewPlans(plans: AnyPlan[], orderedPlansInTabsView: string[]): AnyPlan[] {
  return orderedPlansInTabsView.flatMap((planId) => {
    const plan = plans.find(({ id }) => id === planId);

    return plan ? [plan] : [];
  });
}

export function getPlanVariantNames(plan: Plan): string[] {
  return getServerVisibleVariants(plan)
    .filter((variant) => Boolean(variant.name))
    .map((variant) => variant.name!);
}

export async function pruneStalePlansInTabsView({
  plans,
  selectedTabNames,
  writer,
}: {
  plans: AnyPlan[];
  selectedTabNames: string[];
  writer: PlansInTabsViewWriter;
}): Promise<PlansInTabsViewState> {
  const allowedPlanIds = plans
    .filter((plan) => getIsPlanEligibleForTabsView(plan as Plan, selectedTabNames))
    .map((plan) => plan.id!);

  return writer.pruneStalePlans(allowedPlanIds);
}
